import { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";

export interface MenuBarAction {
  id?: string;
  label: string;
  shortcut?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  separator?: boolean;
  checked?: boolean;
}

export interface MenuBarMenu {
  label: string;
  items: MenuBarAction[];
}

interface MenuBarProps {
  menus: MenuBarMenu[];
  className?: string;
}

export function MenuBar({ menus, className }: MenuBarProps) {
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!barRef.current?.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleTriggerClick(i: number) {
    setOpenMenu(prev => (prev === i ? null : i));
  }

  function handleMouseEnter(i: number) {
    if (openMenu !== null) setOpenMenu(i);
  }

  function handleAction(action: MenuBarAction) {
    if (action.disabled || action.separator) return;
    action.onClick?.();
    setOpenMenu(null);
  }

  return (
    <div ref={barRef} className={cn("menubar", className)} role="menubar">
      {menus.map((menu, i) => (
        <div key={menu.label} className="menubar-item">
          <button
            className={cn("menubar-trigger", openMenu === i && "menubar-trigger-open")}
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={openMenu === i}
            onClick={() => handleTriggerClick(i)}
            onMouseEnter={() => handleMouseEnter(i)}
          >
            {menu.label}
          </button>

          {openMenu === i && (
            <div className="menubar-dropdown" role="menu">
              {menu.items.map((action, j) =>
                action.separator ? (
                  <div key={j} className="menubar-separator" role="separator" />
                ) : (
                  <button
                    key={j}
                    className={cn("menubar-action", action.disabled && "menubar-action-disabled")}
                    role="menuitem"
                    disabled={action.disabled}
                    onClick={() => handleAction(action)}
                  >
                    {action.icon && <span className="menubar-action-icon">{action.icon}</span>}
                    <span className="menubar-action-label">{action.label}</span>
                    {action.checked !== undefined && (
                      <span className="menubar-action-check">{action.checked ? "✓" : ""}</span>
                    )}
                    {action.shortcut && (
                      <span className="menubar-action-shortcut">{action.shortcut}</span>
                    )}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
